import React, { useState, useEffect, useCallback, useRef } from "react";
import { customFetch } from "../apiClient";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import DischargeReceiptModal from "./DischargeReceiptModal";
import DiscountAuthorityDropdown from '../components/DiscountAuthorityDropdown';

const fmt = (n, dec = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const today      = () => new Date().toISOString().split("T")[0];
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};

// Case-insensitive key reader
const g = (row, ...keys) => {
  for (const k of keys) {
    if (row[k] !== undefined) return row[k];
    const lower = k.toLowerCase();
    const found = Object.keys(row).find(rk => rk.toLowerCase() === lower);
    if (found !== undefined) return row[found];
  }
  return null;
};

// Adjusted badge colors for light theme
const BADGE = {
  Male:   { bg: "#e0f2fe", color: "#0284c7", border: "#bae6fd" },
  Female: { bg: "#fce7f3", color: "#db2777", border: "#fbcfe8" },
  Other:  { bg: "#f3e8ff", color: "#9333ea", border: "#e9d5ff" },
};

const ROOM_COLORS = {
  "ICU":              { color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  "NICU":             { color: "#ea580c", bg: "#ffedd5", border: "#fed7aa" },
  "EMERGENCY":        { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  "AC":               { color: "#0284c7", bg: "#e0f2fe", border: "#bae6fd" },
  "NON-AC":           { color: "#059669", bg: "#d1fae5", border: "#a7f3d0" },
  "GENERAL":          { color: "#7c3aed", bg: "#f3e8ff", border: "#e9d5ff" },
  "PRIVATE-ICU-ROOM": { color: "#db2777", bg: "#fce7f3", border: "#fbcfe8" },
};
const rc = (cat) => ROOM_COLORS[(cat || "").toUpperCase()] || { color: "#475569", bg: "#f1f5f9", border: "#e2e8f0" };

// ── Summary Card ──────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="sum-card" style={{ "--accent": accent }}>
    <div className="sum-icon" style={{color: accent}}>{icon}</div>
    <div className="sum-body">
      <div className="sum-val" style={{color: accent}}>{value}</div>
      <div className="sum-label">{label}</div>
      {sub && <div className="sum-sub">{sub}</div>}
    </div>
    <div className="sum-glow" />
  </div>
);

// ── KV helper ────────────────────────────────────────────────
const KV = ({ k, v, highlight, teal, red, green, warning }) => (
  <div className={`detail-kv${highlight?" highlight":""}${teal?" teal-kv":""}${red?" red-kv":""}${green?" green-kv":""}${warning?" warning-kv":""}`}>
    <span>{k}</span><span>{v}</span>
  </div>
);

// ── Room badge ───────────────────────────────────────────────
const RoomBadge = ({ room, compact }) => {
  const cat    = g(room, "roomCategory") || "";
  const clr    = rc(cat);
  const active = g(room, "isActive");
  return (
    <div className={`room-badge-chip${compact ? " room-badge-compact" : ""}`}
      style={{ background: clr.bg, border: `1px solid ${clr.border}` }}>
      <i className={`bi bi-door-${active ? "closed-fill" : "open-fill"}`} style={{ color: clr.color }} />
      <span style={{ color: clr.color, fontWeight: 700 }}>{g(room, "roomNo")}</span>
      <span className="room-cat-small" style={{ color: clr.color, opacity: 0.8 }}>{cat}</span>
      {active ? <span className="live-dot-sm">●</span> : null}
    </div>
  );
};

// ── Services Popup ────────────────────────────────────────────
const ServicesPopup = ({ ipdId, services, patientName, onClose }) => {
  const list  = services.filter(s => String(g(s, "iPD_ID", "ipd_ID", "IPD_ID")) === String(ipdId));
  const total = list.reduce((sum, s) => sum + Number(g(s, "amount") || 0), 0);
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-box" onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-title"><i className="bi bi-clipboard2-pulse-fill" /> Services — {patientName}</div>
          <button className="popup-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>
        <div className="popup-body">
          {list.length === 0
            ? <div className="popup-empty"><i className="bi bi-inbox" /> No services found</div>
            : <>
                <table className="popup-table">
                  <thead><tr><th>#</th><th>Service</th><th>Date</th><th>Qty</th><th>Rate</th><th>Amount</th><th>Unit</th><th>Remarks</th></tr></thead>
                  <tbody>
                    {list.map((s, i) => (
                      <tr key={g(s,"servSrNo") || i}>
                        <td>{i+1}</td>
                        <td><strong>{g(s,"serviceName")||"—"}</strong></td>
                        <td>{fmtDate(g(s,"servdate"))}</td>
                        <td>{g(s,"qty")??1}</td>
                        <td>₹{fmt(g(s,"servRate"))}</td>
                        <td className="amt-green">₹{fmt(g(s,"amount"))}</td>
                        <td>{g(s,"unit")||"—"}</td>
                        <td>{g(s,"remarks")||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="total-label">Total ({list.length} entries)</td>
                      <td className="amt-green total-val">₹{fmt(total)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </>
          }
        </div>
      </div>
    </div>
  );
};

// ── Advance Popup ─────────────────────────────────────────────
const AdvancePopup = ({ ipdId, advances, patientName, onClose }) => {
  const list  = advances.filter(a => String(g(a, "iPD_ID", "ipd_ID", "IPD_ID")) === String(ipdId));
  const total = list.reduce((sum, a) => sum + Number(g(a,"adv_Amt","advAmt")||0), 0);
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-box" onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-title"><i className="bi bi-cash-coin" /> Advance Payments — {patientName}</div>
          <button className="popup-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>
        <div className="popup-body">
          {list.length === 0
            ? <div className="popup-empty"><i className="bi bi-inbox" /> No advance payments</div>
            : <>
                <div className="adv-total-banner">
                  <i className="bi bi-wallet2" />
                  Total Advance Paid: <strong>₹{fmt(total)}</strong>
                  <span className="adv-count-badge">{list.length} payment{list.length > 1 ? "s" : ""}</span>
                </div>
                <table className="popup-table">
                  <thead><tr><th>#</th><th>Amount</th><th>Received Date</th><th>Entry Date</th><th>Mode</th><th>Cheque No</th><th>Received By</th><th>Remarks</th></tr></thead>
                  <tbody>
                    {list.map((a, i) => (
                      <tr key={g(a,"advID")||i}>
                        <td>{i+1}</td>
                        <td className="amt-teal">₹{fmt(g(a,"adv_Amt","advAmt"))}</td>
                        <td>{fmtDateTime(g(a,"recvDate"))}</td>
                        <td>{fmtDateTime(g(a,"eNTRYDATE","entrydate","ENTRYDATE"))}</td>
                        <td><span className="mode-pill">{g(a,"payMode")||"—"}</span></td>
                        <td>{g(a,"chqNo")||"—"}</td>
                        <td>{g(a,"receivedBy")||"—"}</td>
                        <td>{g(a,"remarks")||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={1} className="total-label">Total</td>
                      <td className="amt-teal total-val">₹{fmt(total)}</td>
                      <td colSpan={6} />
                    </tr>
                  </tfoot>
                </table>
              </>
          }
        </div>
      </div>
    </div>
  );
};

// ── Expense Popup ─────────────────────────────────────────────
const ExpensePopup = ({ ipdId, expenses, patientName, onClose }) => {
  const list  = expenses.filter(e => String(g(e, "iPD_ID", "ipd_ID", "IPD_ID")) === String(ipdId));
  const total = list.reduce((sum, e) => sum + Number(g(e,"amount")||0), 0);
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-box" onClick={e => e.stopPropagation()}>
        <div className="popup-header" style={{background: '#fef2f2', borderBottomColor: '#fecaca'}}>
          <div className="popup-title" style={{color: '#dc2626'}}><i className="bi bi-cash-stack" /> Hospital Expenses (Payouts) — {patientName}</div>
          <button className="popup-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>
        <div className="popup-body">
          {list.length === 0
            ? <div className="popup-empty"><i className="bi bi-inbox" /> No expenses recorded for this patient</div>
            : <>
                <div className="adv-total-banner" style={{background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c'}}>
                  <i className="bi bi-wallet2" />
                  Total Hospital Expense: <strong>₹{fmt(total)}</strong>
                  <span className="adv-count-badge" style={{background: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c'}}>{list.length} entry{list.length > 1 ? "s" : ""}</span>
                </div>
                <table className="popup-table">
                  <thead><tr><th>#</th><th>Amount</th><th>Expense Date</th><th>Doctor / Payee</th><th>Mode</th><th>Entry By</th><th>Remarks</th></tr></thead>
                  <tbody>
                    {list.map((e, i) => (
                      <tr key={g(e,"expID")||i}>
                        <td>{i+1}</td>
                        <td className="amt-red" style={{color: '#dc2626', fontWeight: 600}}>₹{fmt(g(e,"amount"))}</td>
                        <td>{fmtDateTime(g(e,"expenseDate"))}</td>
                        <td><strong>{g(e,"doctorName")||"—"}</strong></td>
                        <td><span className="mode-pill" style={{background: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5'}}>{g(e,"payMode")||"—"}</span></td>
                        <td>{g(e,"entryBy")||"—"}</td>
                        <td>{g(e,"remarks")||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={1} className="total-label">Total Expense</td>
                      <td className="total-val" style={{color: '#dc2626'}}>₹{fmt(total)}</td>
                      <td colSpan={5} />
                    </tr>
                  </tfoot>
                </table>
              </>
          }
        </div>
      </div>
    </div>
  );
};

// ── Patient Row ───────────────────────────────────────────────
const PatientRow = ({ row, tab, idx, rooms, services, advances, expenses }) => {
  const [open,    setOpen]    = useState(false);
  const [showSvc, setShowSvc] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [showExp, setShowExp] = useState(false); // ✅ New state for Expense Popup

  const ipdId  = g(row, "iPD_ID", "ipd_ID", "IPD_ID");
  const name   = g(row, "name") || "—";
  const gender = g(row, "gender") || "Other";
  const gBadge = BADGE[gender] || BADGE.Other;

  const patRooms = rooms.filter(r => String(g(r,"iPD_ID","ipd_ID","IPD_ID")) === String(ipdId));
  const patAdv   = advances.filter(a => String(g(a,"iPD_ID","ipd_ID","IPD_ID")) === String(ipdId));
  const totalAdv = patAdv.reduce((s, a) => s + Number(g(a,"adv_Amt","advAmt")||0), 0);
  
  // ✅ Extract Expenses Data
  const patExp   = expenses.filter(e => String(g(e,"iPD_ID","ipd_ID","IPD_ID")) === String(ipdId));
  const totalExp = Number(g(row,"totalHospitalExpense") || 0);

  const svcCount = Number(g(row,"serviceCount") ?? 0);
  const estDue   = Number(g(row,"estimatedDue")  ?? 0);
  
  // Billing and Medicine values
  const billGross = Number(g(row,"billingGrossAmount") || 0);
  const billDiscount = Number(g(row,"billingDiscountAmount") || 0);
  const billDue  = Number(g(row,"billingDueAmount", "billingDue", "billDue") || 0);
  const billPayable = Number(g(row,"billingPayableAmount", "billingPayable") || 0);
  const billPaid = Number(g(row,"billingPaidAmount", "billingPaid") || 0);

  const medGross = Number(g(row,"medicineGrossAmount") || 0);
  const medDiscount = Number(g(row,"medicineDiscountAmount") || 0);
  const medPayable = Number(g(row,"medicinePayableAmount", "totalMedicineAmount") || 0);
  const medPaid = Number(g(row,"medicinePaidAmount") || 0);
  const medDue = Number(g(row,"medicineDueAmount") || 0);

  const dischargeGross = Number(g(row, "totalAmount") || 0);
  const dischargeDiscount = Number(g(row, "billDiscount") || 0);
  const dischargePayable = Number(g(row, "billPayable") || 0);
  const dischargePaid = Number(g(row, "billReceived") || 0);
  const dischargeDue = Number(g(row, "billDue") || 0);
  
  // Package Data Tracking
  const isPkg       = !!g(row,"packageId");
  const pkgAmt      = Number(g(row,"packageAmount") || 0);
  const isMedInc    = Boolean(g(row,"isMedicineBillingIncluded"));
  const isBillInc   = Boolean(g(row,"isBillingIncluded") || g(row,"isBillingIncluded") === undefined);
  
  const svcUsed     = Number(g(row,"totalServiceAmount") || 0);
  const roomUsed    = Number(g(row,"totalRoomCharges") || 0);
  
  // Margin Calculation (Hospital Point of view - Expenses)
  // ✅ NEW: Minus totalExp from the margin
  const pkgRemAmt   = pkgAmt - svcUsed - roomUsed - (isMedInc ? medPayable : 0) - (isBillInc ? billPayable : 0) - totalExp;

  const regConsult     = Number(g(row,"regConsultFee")??0);
  const regDiscountPct = Number(g(row,"regDiscount")??0); 
  const regDiscountAmt = regConsult * (regDiscountPct / 100); 
  const regPayable     = Number(g(row,"regPayable")??0);
  const regReceived    = Number(g(row,"regReceived")??0);
  const regDue         = Number(g(row,"regDue")??0);

  return (
    <>
      <tr className={`data-row${open?" row-expanded":""}`}
        onClick={() => setOpen(p => !p)}
        style={{ animationDelay: `${idx * 20}ms` }}>

        {/* Patient */}
        <td>
          <div className="pat-name">
            <span className="pat-srno">#{g(row,"iPD_SRNO","ipD_SRNO","IPD_SRNO")}</span>
            <span>{g(row,"title") ? g(row,"title")+" " : ""}{name}</span>
            {isPkg && <span className="pkg-pill"><i className="bi bi-box-seam" /> {g(row,"packageName")}</span>}
          </div>
          <div className="pat-meta">
            <span className="uhid-tag">UHID: {g(row,"uHID_No","uhid_No","UHID_No","uhiD_No") || "—"}</span>
            <span className="gender-pill" style={{ background: gBadge.bg, color: gBadge.color, border: `1px solid ${gBadge.border}` }}>{gender}</span>
            <span className="age-tag">{g(row,"age")} {g(row,"ageIn")}</span>
          </div>
        </td>

        {/* Contact */}
        <td>
          <div className="mob-wrap"><i className="bi bi-phone" />{g(row,"mobileNo")||"—"}</div>
          {g(row,"careOff") && <div className="care-wrap"><i className="bi bi-person-lines-fill" />{g(row,"careOff")}</div>}
        </td>

        {/* Doctor / Dept */}
        <td>
          <div className="doc-wrap">
            <i className="bi bi-person-badge-fill" />
            {g(row,"doctorName")||"—"}
            {g(row,"doctorDegree") && <span className="deg-tag">({g(row,"doctorDegree")})</span>}
          </div>
          <div className="dept-wrap">{g(row,"departmentName")||"—"}</div>
        </td>

        {/* Dates */}
        <td>
          <div className="date-chip"><i className="bi bi-calendar-event" />{fmtDate(g(row,"regDate"))}</div>
          <div className="los-chip"><i className="bi bi-hourglass-split" />{g(row,"lengthOfStayDays")??0} days</div>
        </td>

        {/* Rooms */}
        <td>
          {patRooms.length === 0
            ? <span className="no-room">No Room</span>
            : patRooms.length === 1
              ? <RoomBadge room={patRooms[0]} />
              : <div className="multi-room">{patRooms.map((r,i) => <RoomBadge key={i} room={r} compact />)}</div>
          }
        </td>

       
    {/* Charges */}
        <td className="amt-col">
          {tab === "current" ? <>
            <div className="amt-row green">
              <span className="amt-label">Svc:</span>₹{fmt(svcUsed)}
              {svcCount > 0 && (
                <button className="svc-count-btn" onClick={e=>{e.stopPropagation();setShowSvc(true);}}>
                  {svcCount} <i className="bi bi-eye-fill" />
                </button>
              )}
            </div>
            <div className="amt-row blue"><span className="amt-label">Room:</span>₹{fmt(roomUsed)}</div>
            <div className="amt-row orange total-row"><span className="amt-label">Gross:</span>₹{fmt(g(row,"grandTotalExpense"))}</div>
          </> : <>
            <div className="amt-row green"><span className="amt-label">Gross:</span>₹{fmt(dischargeGross)}</div>
            <div className="amt-row blue"><span className="amt-label">Disc:</span>-₹{fmt(dischargeDiscount)}</div>
            <div className="amt-row orange total-row"><span className="amt-label">Net:</span>₹{fmt(dischargePayable)}</div>
          </>}
        </td>

        {/* Advance / Due */}
        <td className="adv-col">
          <div className="amt-row teal">
            <span className="amt-label">{tab === "current" ? "Adv:" : "Recv:"}</span>₹{fmt(tab === "current" ? g(row,"totalPaidAmount") : dischargePaid)}
          </div>
          {tab === "current"
            ? <div className={`amt-row ${estDue>0?"red":"green"} total-row`}><span className="amt-label">Est Due:</span>₹{fmt(estDue)}</div>
            : <>
                <div className={`amt-row ${dischargeDue>0?"red":"green"} total-row`}><span className="amt-label">Final Due:</span>₹{fmt(dischargeDue)}</div>
              </>
          }
        </td>

        {/* Status / Expand */}
        <td className="expand-col">
          {tab === "discharged" && (
            <span className={`status-pill ${g(row,"patDieOrLive")?"deceased":"recovered"}`}>
              {g(row,"patDieOrLive") ? <><i className="bi bi-heartbreak" /> Exp.</> : <><i className="bi bi-heart-pulse" /> Alive</>}
            </span>
          )}
          <button className="expand-btn" onClick={e=>{e.stopPropagation();setOpen(p=>!p);}}>
            <i className={`bi bi-chevron-${open?"up":"down"}`} />
          </button>
        </td>
      </tr>

      {/* Expanded detail */}
      {open && (
        <tr className="detail-row">
          <td colSpan={8}>
            <div className="detail-grid">

              {/* Patient */}
              <div className="detail-block">
                <div className="detail-block-title"><i className="bi bi-person-vcard" /> Patient Info</div>
                <KV k="Address"  v={g(row,"address")||"—"} />
                <KV k="City"     v={g(row,"city")||"—"} />
                <KV k="Problems" v={g(row,"problems")||"—"} />
                {g(row,"careOff") && <KV k="Care Of" v={`${g(row,"careOff")} (${g(row,"relationWithPatient")||""})`} />}
                {g(row,"alternateMobileNo") && <KV k="Alt. Mobile" v={g(row,"alternateMobileNo")} />}
              </div>

              {/* Financials & Billing - CURRENT TAB ONLY */}
              {tab === "current" && (
                <>
                  <div className="detail-block">
                    <div className="detail-block-title"><i className="bi bi-file-earmark-text" /> Registration Billing</div>
                    <KV k="Consult Fee"  v={`₹${fmt(regConsult)}`} />
                    <KV k="Discount"     v={`${fmt(regDiscountPct, 0)}% (-₹${fmt(regDiscountAmt)})`} />
                    <KV k="Payable"      v={`₹${fmt(regPayable)}`} highlight />
                    <KV k="Received"     v={`₹${fmt(regReceived)}`} teal />
                    <KV k="Due"          v={`₹${fmt(regDue)}`} red={regDue>0} green={regDue<=0} />
                  </div>

                  <div className="detail-block">
                    <div className="detail-block-title"><i className="bi bi-cash-stack" /> Overall IPD Financials</div>
                    <KV k="Services Amount" v={`₹${fmt(svcUsed)}`} />
                    <KV k="Room Charges"    v={`₹${fmt(roomUsed)}`} />
                    <KV k="Reg Consult Fee" v={`₹${fmt(regConsult)}`} />
                    <KV k="Gross Total"     v={`₹${fmt(g(row,"grandTotalExpense"))}`} highlight />
                    <KV k="Reg Discount"    v={`-₹${fmt(regDiscountAmt)}`} />
                    <KV k="Total Paid"      v={`₹${fmt(g(row,"totalPaidAmount"))}`} teal />
                    <KV k="Est. Due"        v={`₹${fmt(estDue)}`} red={estDue>0} green={estDue<=0} />
                  </div>
                </>
              )}

              {/* GENERAL BILLING BLOCK (FOR BOTH TABS) */}
              <div className="detail-block">
                <div className="detail-block-title"><i className="bi bi-receipt" /> General Billing</div>
                {tab === "discharged" && <KV k="Bill No"      v={g(row,"billNo")||"—"} />}
                {tab === "discharged" && <KV k="Discharge"    v={fmtDate(g(row,"dischargeDate"))} />}
                <KV k="Gross Amount" v={`₹${fmt(billGross)}`} />
                <KV k="Discount"     v={`₹${fmt(billDiscount)}`} />
                <KV k="Payable"      v={`₹${fmt(billPayable)}`} highlight />
                <KV k="Total Paid"   v={`₹${fmt(billPaid)}`} teal />
                <KV k="Due"          v={`₹${fmt(billDue)}`} red={billDue>0} green={billDue<=0} />
              </div>

              {tab === "discharged" && (
                <div className="detail-block">
                  <div className="detail-block-title"><i className="bi bi-file-earmark-check" /> Final Discharge Bill</div>
                  <KV k="Bill No"       v={g(row,"billNo")||"—"} />
                  <KV k="Discharge Date" v={fmtDate(g(row,"dischargeDate"))} />
                  <KV k="Gross Amount"  v={`₹${fmt(dischargeGross)}`} />
                  <KV k="Discount Given" v={`₹${fmt(dischargeDiscount)}`} />
                  <KV k="Net Payable"   v={`₹${fmt(dischargePayable)}`} highlight />
                  <KV k="Received at Discharge" v={`₹${fmt(dischargePaid)}`} teal />
                  <KV k="Final Due"     v={`₹${fmt(dischargeDue)}`} red={dischargeDue>0} green={dischargeDue<=0} />
                </div>
              )}

              <div className="detail-block">
                <div className="detail-block-title"><i className="bi bi-capsule" /> Medicine Sales</div>
                <KV k="Gross Amount" v={`₹${fmt(medGross)}`} />
                <KV k="Discount"     v={`₹${fmt(medDiscount)}`} />
                <KV k="Payable"      v={`₹${fmt(medPayable)}`} highlight />
                <KV k="Total Paid"   v={`₹${fmt(medPaid)}`} teal />
                <KV k="Due"          v={`₹${fmt(medDue)}`} red={medDue>0} green={medDue<=0} />
              </div>

              {/* ✅ PACKAGE TRACKING BLOCK WITH MARGIN */}
              {isPkg && (
                <div className="detail-block pkg-block">
                  <div className="detail-block-title"><i className="bi bi-box-seam-fill" /> Package Tracking</div>
                  <KV k="Package Name" v={g(row,"packageName")} />
                  <KV k="Package Amt"  v={`₹${fmt(pkgAmt)}`} highlight />
                  
                  <div className="pkg-breakdown">
                    <KV k="(-) Services Used" v={`₹${fmt(svcUsed)}`} red />
                    <KV k="(-) Room Used"     v={`₹${fmt(roomUsed)}`} red />
                    {isBillInc ? (
                      <KV k="(-) Gen. Billing" v={`₹${fmt(billPayable)}`} red />
                    ) : (
                      <KV k="(i) Gen. Billing" v="Not Included" warning />
                    )}
                    {isMedInc ? (
                      <KV k="(-) Medicine Used" v={`₹${fmt(medPayable)}`} red />
                    ) : (
                      <KV k="(i) Medicine" v="Not Included" warning />
                    )}
                    {/* ✅ SHOW EXPENSE DEDUCTION */}
                    <KV k="(-) Hosp. Expense" v={`₹${fmt(totalExp)}`} red />
                  </div>
                  
                  <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #fcd34d' }}>
                    <KV k="Hospital Margin" v={`₹${fmt(pkgRemAmt)}`} green={pkgRemAmt >= 0} red={pkgRemAmt < 0} />
                  </div>
                </div>
              )}

              {/* ✅ NEW: HOSPITAL EXPENSES BLOCK */}
              <div className="detail-block" style={{background: '#fef2f2', borderColor: '#fecaca'}}>
                <div className="detail-block-title" style={{color: '#dc2626', borderBottomColor: '#fecaca'}}>
                  <i className="bi bi-cash-stack" /> Hospital Expenses
                  <span className="detail-total-badge" style={{background: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5'}}>₹{fmt(totalExp)}</span>
                </div>
                {patExp.length === 0 ? (
                  <div className="no-data-small" style={{color: '#ef4444'}}>No expenses recorded</div>
                ) : (
                  <>
                    <KV k="Expense Entries" v={`${patExp.length} payment(s)`} />
                    <KV k="Total Payouts" v={`₹${fmt(totalExp)}`} red />
                    <button className="btn-view-svc" onClick={()=>setShowExp(true)} style={{background: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5', marginTop: '10px'}}>
                      <i className="bi bi-eye" /> View Expense Details
                    </button>
                  </>
                )}
              </div>

              {/* Room breakdown */}
              <div className="detail-block">
                <div className="detail-block-title"><i className="bi bi-hospital" /> Rooms Occupied</div>
                {patRooms.length === 0
                  ? <div className="no-data-small">No room assigned</div>
                  : patRooms.map((r, i) => {
                      const cat    = g(r,"roomCategory")||"";
                      const clr    = rc(cat);
                      const days   = g(r,"daysStayed")??0;
                      const charge = g(r,"roomCharge") ??0;
                      const active = g(r,"isActive");
                      return (
                        <div key={i} className="room-detail-row" style={{ borderLeft: `3px solid ${clr.color}` }}>
                          <div className="room-detail-top">
                            <span className="room-cat-badge" style={{ background: clr.bg, color: clr.color, border: `1px solid ${clr.border}` }}>{cat||"Room"}</span>
                            <span className="room-no-tag">{g(r,"roomNo")}</span>
                            {active ? <span className="live-dot">● Live</span> : null}
                          </div>
                          <div className="room-detail-meta">
                            <span>{fmtDate(g(r,"entryDate"))} → {g(r,"exitDate")?fmtDate(g(r,"exitDate")):"Present"}</span>
                            <span className="room-days">{days} day{days!==1?"s":""} × ₹{fmt(g(r,"roomRate"))}/day</span>
                            <span className="room-charge-tag">= ₹{fmt(charge)}</span>
                          </div>
                        </div>
                      );
                    })
                }
                {patRooms.length > 1 && (
                  <div className="room-total-row">
                    <span>Total Room Charges</span>
                    <span>₹{fmt(patRooms.reduce((s,r)=>s+Number(g(r,"roomCharge")||0),0))}</span>
                  </div>
                )}
              </div>

              {/* Services summary */}
              <div className="detail-block">
                <div className="detail-block-title"><i className="bi bi-clipboard2-pulse" /> Services</div>
                <KV k="Count"     v={`${svcCount} entries`} />
                <KV k="Total Amt" v={`₹${fmt(svcUsed)}`} />
                {tab === "current" && g(row,"lastServiceDate") && <KV k="Last Added" v={fmtDate(g(row,"lastServiceDate"))} />}
                {svcCount > 0 && (
                  <button className="btn-view-svc" onClick={()=>setShowSvc(true)}>
                    <i className="bi bi-eye" /> View All Services
                  </button>
                )}
              </div>

              {/* Advance history */}
              <div className="detail-block">
                <div className="detail-block-title">
                  <i className="bi bi-cash-coin" /> Advance Payments
                  <span className="detail-total-badge">₹{fmt(totalAdv)}</span>
                </div>
                {patAdv.length === 0
                  ? <div className="no-data-small">No advance payments</div>
                  : patAdv.map((a,i) => (
                      <div className="adv-detail-row" key={i}>
                        <div className="adv-detail-top">
                          <span className="adv-amt">₹{fmt(g(a,"adv_Amt","advAmt"))}</span>
                          <span className="mode-pill">{g(a,"payMode")||"Cash"}</span>
                              <button className="adv-count-btn" onClick={e=>{e.stopPropagation();setShowAdv(true);}}>
                {patAdv.length} <i className="bi bi-eye-fill" />
              </button>
                        </div>
                        <div className="adv-detail-date"><i className="bi bi-calendar-check" />{fmtDateTime(g(a,"recvDate"))}</div>
                        {g(a,"remarks") && <div className="adv-remarks">{g(a,"remarks")}</div>}
                     
                      </div>

                      
             
          
                    ))
                    
                }
              </div>

            </div>
          </td>
        </tr>
      )}

      {showSvc && <ServicesPopup ipdId={ipdId} services={services} patientName={name} onClose={()=>setShowSvc(false)} />}
      {showAdv && <AdvancePopup  ipdId={ipdId} advances={advances} patientName={name} onClose={()=>setShowAdv(false)} />}
      {showExp && <ExpensePopup  ipdId={ipdId} expenses={expenses} patientName={name} onClose={()=>setShowExp(false)} />}
    </>
  );
};

// ── Main ─────────────────────────────────────────────────────
const IpdReport = () => {
  const [tab,      setTab]      = useState("current");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [data,     setData]     = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [rooms,    setRooms]    = useState([]);
  const [services, setServices] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]); // ✅ NEW: Expenses State

  // Discharge Form States (for the 70-30 layout when searching individual patient)
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [finSummary, setFinSummary] = useState(null);
  const [showDischargeForm, setShowDischargeForm] = useState(false);
  const [dischargingPatient, setDischargingPatient] = useState(false);
  const [message, setMessage] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  const [dischargeForm, setDischargeForm] = useState({
    billDate: new Date().toISOString().substring(0, 10),
    dischargeDate: new Date().toISOString().substring(0, 10),
    remarks: "",
    totalAmount: 0,
    discountType: "amount",
    discountValue: 0,
    discount: 0,
    advance: 0,
    payable: 0,
    received: 0,
    due: 0,
    disAuthority: "",
    docPreparedby: "",
    patientStatus: false,
    nofDays: 1,
  });

  const formRef = useRef(null);

  const [filters, setFilters] = useState({ from:"", to:today(), search:"", doctorId:null, deptId:null });
  const [doctors,     setDoctors]     = useState([]);
  const [departments, setDepartments] = useState([]);

  // ==================== KEYBOARD NAVIGATION ====================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.closest('.react-select__menu')) return;
      if (e.target.closest('.react-select__input-container')) return;

      const focusableSelector =
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        formRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;

      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        formRef.current?.requestSubmit();
        return;
      }

      if (currentIndex >= 0) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    customFetch("/Departments").then(r=>r.json()).then(d=>setDepartments(d.map(x=>({value:x.srno,label:x.departmentName})))).catch(()=>{});
    customFetch("/Employees/doctors").then(r=>r.json()).then(d=>setDoctors(d.map(x=>({value:x.id,label:x.name})))).catch(()=>{});
    customFetch("/Company/1").then(r=>r.json()).then(d=>setCompanyDetails(d)).catch(()=>{});
  }, []);



// 🖨️ PROFESSIONAL PRINT FUNCTION
  const handlePrintList = () => {
    if (data.length === 0) {
      alert("No data available to print!");
      return;
    }

    const isCurrent = tab === "current";
    const title = isCurrent ? "CURRENTLY ADMITTED PATIENTS" : "DISCHARGED PATIENTS REPORT";

    // 1. Columns Setup based on your requirement
    let thHtml = isCurrent
      ? `<th style="width:5%">S.No</th>
         <th style="text-align:left; width:20%">Patient Name</th>
         <th style="width:10%">Age/Gen</th>
         <th style="width:10%">Date</th>
         <th style="text-align:left; width:20%">Doctor</th>
         <th style="width:10%">Mobile</th>
         <th style="width:15%">Room No</th>
         <th style="text-align:right; width:10%">Due (₹)</th>`
      : `<th style="width:5%">S.No</th>
         <th style="text-align:left; width:20%">Patient Name</th>
         <th style="width:10%">Age/Gen</th>
         <th style="width:10%">Date</th>
         <th style="text-align:left; width:20%">Doctor</th>
         <th style="width:10%">Mobile</th>
         <th style="width:15%">Room No</th>
         <th style="text-align:center; width:10%">Status</th>`;

    // 2. Rows Setup
    const trHtml = data.map((row, idx) => {
      const name = g(row, "name") || "—";
      const age = g(row, "age") || "";
      const gender = g(row, "gender") ? String(g(row, "gender")).charAt(0) : "";
      const date = fmtDate(isCurrent ? g(row, "regDate") : g(row, "dischargeDate"));
      const doc = g(row, "doctorName") || "—";
      const mobile = g(row, "mobileNo") || "—";
      
      // Find Room for this patient
      const patRooms = rooms.filter(r => String(g(r,"iPD_ID","ipd_ID","IPD_ID")) === String(g(row, "iPD_ID")));
      const roomNo = patRooms.length > 0 ? patRooms.map(r => g(r, "roomNo")).join(", ") : "—";

      if (isCurrent) {
        const estDue = Number(g(row,"estimatedDue") || 0);
        return `<tr>
          <td style="text-align:center">${idx + 1}</td>
          <td><strong>${name}</strong></td>
          <td style="text-align:center">${age}/${gender}</td>
          <td style="text-align:center">${date}</td>
          <td>${doc}</td>
          <td style="text-align:center">${mobile}</td>
          <td style="text-align:center">${roomNo}</td>
          <td style="text-align:right; font-weight:bold; color:#dc2626;">${fmt(estDue)}</td>
        </tr>`;
      } else {
         const isDead = g(row,"patDieOrLive");
         const status = isDead ? "Death" : "Discharged";
         return `<tr>
          <td style="text-align:center">${idx + 1}</td>
          <td><strong>${name}</strong></td>
          <td style="text-align:center">${age}/${gender}</td>
          <td style="text-align:center">${date}</td>
          <td>${doc}</td>
          <td style="text-align:center">${mobile}</td>
          <td style="text-align:center">${roomNo}</td>
          <td style="text-align:center; font-weight:bold;">${status}</td>
        </tr>`;
      }
    }).join('');

    // 3. Generate HTML & Print
    const printWindow = window.open("", "_blank", "width=1000,height=700");
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
             * { box-sizing: border-box; }
             body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
             .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 15px; }
             .hosp-name { font-size: 22px; font-weight: bold; margin-bottom: 5px; }
             .title { font-size: 16px; font-weight: bold; text-transform: uppercase; background: #e2e8f0; display: inline-block; padding: 5px 15px; border-radius: 4px; margin-top: 5px;}
             .meta { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 10px; color: #555; }
             table { width: 100%; border-collapse: collapse; font-size: 12px; }
             th { background-color: #f1f5f9; padding: 8px; border: 1px solid #cbd5e1; font-weight: bold; -webkit-print-color-adjust: exact; }
             td { padding: 8px; border: 1px solid #cbd5e1; vertical-align: middle; }
             tr:nth-child(even) { background-color: #f8fafc; -webkit-print-color-adjust: exact; }
          </style>
        </head>
        <body>
           <div class="header">
              <div class="hosp-name">${companyDetails ? companyDetails.company_Name : "HOSPITAL REPORT"}</div>
              <div class="title">${title}</div>
           </div>
           <div class="meta">
              <span><strong>Total Records:</strong> ${data.length}</span>
              <span><strong>Printed On:</strong> ${new Date().toLocaleString('en-IN')}</span>
           </div>
           <table>
              <thead><tr>${thHtml}</tr></thead>
              <tbody>${trHtml}</tbody>
           </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 400); // Thoda delay taaki CSS load ho jaye
  };



  useEffect(() => {
    setFilters(f=>({ ...f, from: tab==="current"?"":monthStart(), to:today() }));
    setData([]); setSummary(null); setRooms([]); setServices([]); setAdvances([]); setExpenses([]);
    setSelectedPatient(null); setShowDischargeForm(false); setFinSummary(null);
  }, [tab]);

  // Load patient list for dropdown
  const loadIpdOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
     const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      
      return data.map((item) => {
        const regDateFormatted = item.regDate || item.ipD_Date
            ? new Date(item.regDate || item.ipD_Date).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";
        return {
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
          raw: item
        };
      });
    } catch { 
      return []; 
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const p = new URLSearchParams();
      if (filters.from)     p.set("from",     filters.from);
      if (filters.to)       p.set("to",       filters.to);
      if (filters.search)   p.set("search",   filters.search.trim());
      if (filters.doctorId) p.set("doctorId", filters.doctorId);
      if (filters.deptId)   p.set("deptId",   filters.deptId);
      const ep  = tab==="current" ? "current-admitted" : "discharged";
      const res = await customFetch(`/IpdReport/${ep}?${p.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data||[]); setSummary(json.summary||null);
      setRooms(json.rooms||[]); setServices(json.services||[]); setAdvances(json.advances||[]);
      setExpenses(json.expenses||[]); // ✅ Store expenses data
    } catch(e) { setError(e.message||"Failed"); } finally { setLoading(false); }
  }, [tab, filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Fetch data for individual selected patient
  const fetchFinancialSummary = async (ipdId) => {
    try {
      const res = await customFetch(`/DischargeDt/ipd-financial-summary/${ipdId}`);
      if (res.ok) {
        const d = await res.json();
        if(d.billingHistory) d.billingHistory.sort((a,b) => new Date(a.date) - new Date(b.date));
        if(d.medicineHistory) d.medicineHistory.sort((a,b) => new Date(a.date) - new Date(b.date));
        setFinSummary(d);
      }
    } catch (e) { console.error("Fin summary fetch failed", e); }
  };

  useEffect(() => {
    if (!selectedPatient) {
      setFinSummary(null); setShowDischargeForm(false); return;
    }
    setError(null); setMessage(null); setShowDischargeForm(true);
    fetchFinancialSummary(selectedPatient.value);
    
    // We also need to fetch advance, services, rooms for this patient to show in the UI below form
    customFetch(`/IpdAdvDt/search-by-ipd?ipdId=${selectedPatient.value}`).then(r=>r.json()).then(setAdvances).catch(()=>{});
    customFetch(`/IPDServiceDetailDT/by-ipd/${selectedPatient.value}`).then(r=>r.json()).then(setServices).catch(()=>{});
    customFetch(`/IPDRoom/search-by-ipd?rIpdId=${selectedPatient.value}`).then(r=>r.json()).then(setRooms).catch(()=>{});
    
    // ✅ Search ExPDT expenses for this IPD ID specifically
    customFetch(`/ExPDT/Search?search=${selectedPatient.value}`).then(r=>r.json()).then(data => {
        // Just filtering manually to be safe if backend search is broad
        const filtered = data.filter(e => e.patientType === "IPD" && e.refId === selectedPatient.value);
        setExpenses(filtered);
    }).catch(()=>{});

  }, [selectedPatient]);

  // Discharge Calculations
  useEffect(() => {
    if (!selectedPatient) return;

    const totalFromServices = services.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const totalFromRooms = rooms.reduce((sum, room) => {
      if (!room.entryDate) return sum;
      const entry = new Date(room.entryDate);
      let exit = room.exitDate ? new Date(room.exitDate) : new Date(dischargeForm.dischargeDate);
      if (!room.exitDate) {
        const now = new Date();
        exit.setHours(now.getHours(), now.getMinutes(), 0, 0);
      }
      const diffTime = exit.getTime() - entry.getTime();
      let days = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
      return sum + (days * (Number(room.roomRate) || 0));
    }, 0);
    const totalAdvance = advances.reduce((sum, a) => sum + (Number(a.adv_Amt || a.adv_amt || a.amount) || 0), 0);

    let grandTotal = 0;
    let finalPayable = 0;

    if (finSummary && finSummary.hasPackage) {
        grandTotal = Number(finSummary.packageAmount) || 0;
        let deductions = totalAdvance;
        if (finSummary.isBillingIncluded) deductions += Number(finSummary.billingPaid || 0);
        if (finSummary.isMedicineIncluded) deductions += Number(finSummary.medicinePaid || 0);

        let tempDiscount = dischargeForm.discountType === "percent"
          ? (grandTotal * Number(dischargeForm.discountValue)) / 100
          : Number(dischargeForm.discountValue);

        finalPayable = grandTotal - tempDiscount - deductions;
    } else {
        grandTotal = totalFromServices + totalFromRooms;
        let tempDiscount = dischargeForm.discountType === "percent"
          ? (grandTotal * Number(dischargeForm.discountValue)) / 100
          : Number(dischargeForm.discountValue);
        finalPayable = grandTotal - tempDiscount - totalAdvance;
    }

    let calculatedDiscount = dischargeForm.discountType === "percent"
      ? (grandTotal * Number(dischargeForm.discountValue)) / 100
      : Number(dischargeForm.discountValue);
    const dueAmount = finalPayable - Number(dischargeForm.received);

    let calculatedDays = 1;
    if (selectedPatient.raw) {
        const rawDate = selectedPatient.raw.regDate || selectedPatient.raw.ipD_Date;
        if (rawDate) {
            const diffDays = Math.ceil((new Date(dischargeForm.dischargeDate) - new Date(rawDate)) / (1000 * 60 * 60 * 24));
            calculatedDays = Math.max(1, diffDays);
        }
    }

    setDischargeForm((prev) => ({
      ...prev,
      totalAmount: grandTotal, 
      advance: totalAdvance,
      serviceTotal: totalFromServices,
      roomTotal: totalFromRooms,
      discount: calculatedDiscount,
      payable: finalPayable > 0 ? finalPayable : 0,
      due: dueAmount,
      nofDays: calculatedDays,
    }));
  }, [services, rooms, advances, dischargeForm.discountType, dischargeForm.discountValue, dischargeForm.received, dischargeForm.dischargeDate, selectedPatient, finSummary]);

  const handleDischargeFormChange = (f, v) => setDischargeForm(p => ({ ...p, [f]: v }));
  
  const handleDischargeSubmit = async () => {
    if (!selectedPatient) return setError("Select patient first");
    if (!dischargeForm.docPreparedby) return setError("Prepared By is required");

    if (dischargeForm.discount > 0 && !dischargeForm.disAuthority) {
      return setError("Discount diya gaya hai, isliye Dis. Authority select karna zaroori hai.");
    }
    setDischargingPatient(true); setError("");
    try {
      const payload = {
        IPDID: selectedPatient.value,
        disdate: dischargeForm.dischargeDate,
        invPref: "BILL",
        patientsSTS: dischargeForm.patientStatus ? "Dead" : "Alive",
        remarks: dischargeForm.remarks,
        patDieOrLive: dischargeForm.patientStatus,
        totalAmount: dischargeForm.totalAmount,
        discount: dischargeForm.discount,
        payable: dischargeForm.payable,
        received: dischargeForm.received,
        due: dischargeForm.due,
        invDates: dischargeForm.billDate,
        nofdays: String(dischargeForm.nofDays),
        docPreparedby: dischargeForm.docPreparedby,
        hid: Number(sessionStorage.getItem("hospitalId")) || 1,
        syncSts: false,
        disAuthority: dischargeForm.disAuthority,
      };

     const res = await customFetch(`/DischargeDt`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
     });
      if (!res.ok) throw new Error(await res.text() || "Failed");
      const savedRecord = await res.json();
      
      const receiptObj = {
        billDate: dischargeForm.billDate,
        dischargeDate: dischargeForm.dischargeDate,
        patient: selectedPatient.raw,
        services: services,
        advanceDetails: advances,
        roomDetails: rooms,
        totalAmount: dischargeForm.totalAmount,
        discount: dischargeForm.discount,
        payable: dischargeForm.payable,
        received: Number(dischargeForm.received || 0),
        due: dischargeForm.due,
        rawServerResponse: savedRecord,
      };

      setReceiptData(receiptObj);
      setShowReceipt(true);
      setMessage("Patient discharged successfully!");
      setDischargeForm(prev => ({ ...prev, discountValue: 0, received: 0, docPreparedby: "" }));
      fetchFinancialSummary(selectedPatient.value);
    } catch (e) { setError(e.message); } finally { setDischargingPatient(false); }
  };

  const hasPendingDues = finSummary && (Number(finSummary.billingDue) > 0 || Number(finSummary.medicineDue) > 0);
  
  // ✅ Calculate overall hospital expense to minus from margin
  const rightPanelTotalExpense = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const pkgExpenseTotal = (dischargeForm.serviceTotal || 0) + (dischargeForm.roomTotal || 0) + (finSummary?.isBillingIncluded ? (finSummary.billingPayable || 0) : 0) + (finSummary?.isMedicineIncluded ? (finSummary.medicinePayable || 0) : 0) + rightPanelTotalExpense;
  const pkgMargin = (finSummary?.packageAmount || 0) - pkgExpenseTotal;

  const ss = {
    control: (b, s) => ({...b, minHeight: 32, height: 32, fontSize: 12, borderRadius: 6, background: "#ffffff", borderColor: s.isFocused ? "var(--accent)" : "#cbd5e1", color: "#0f172a", boxShadow: "none", "&:hover": {borderColor: "var(--accent)"}}),
    singleValue: b => ({...b, color: "#0f172a"}),
    placeholder: b => ({...b, color: "#94a3b8", fontSize: 12}),
    input: b => ({...b, color: "#0f172a", margin: 0, padding: 0}),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: b => ({...b, background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 10px 25px rgba(0,0,0,0.1)"}),
    option: (b, s) => ({...b, fontSize: 12, padding: "8px 12px", background: s.isSelected ? "var(--accent)" : s.isFocused ? "#f1f5f9" : "transparent", color: s.isSelected ? "#ffffff" : "#0f172a", cursor: "pointer"}),
    dropdownIndicator: b => ({...b, padding: "0 6px", color: "#94a3b8"}),
    clearIndicator: b => ({...b, padding: "0 4px", color: "#94a3b8"}),
    indicatorSeparator: () => ({display: "none"}),
    valueContainer: b => ({...b, padding: "0 8px"}),
  };

  const headerSelectStyles = {
    ...ss,
    control: (b, s) => ({ ...b, minHeight: 32, height: 32, fontSize: 12, borderRadius: 6, background: 'rgba(255,255,255,0.15)', borderColor: s.isFocused ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', color: "#fff", "&:hover": {borderColor: 'rgba(255,255,255,0.7)'} }),
    singleValue: b => ({...b, color: "#fff"}),
    placeholder: b => ({...b, color: "rgba(255,255,255,0.65)"}),
    input: b => ({...b, color: "#fff"}),
    dropdownIndicator: b => ({...b, color: 'rgba(255,255,255,0.7)'}),
    clearIndicator: b => ({...b, color: 'rgba(255,255,255,0.7)'}),
  };

  const currentCards = summary ? [
    {icon:"🏥",label:"Total Admitted",  value:summary.totalAdmitted??0,                 accent:"#0284c7"},
    {icon:"📝",label:"Reg Consult Fees", value:"₹"+fmt(summary.totalRegConsultFee),      accent:"#7c3aed", sub: "Admission time"},
    {icon:"🔧",label:"Services Revenue", value:"₹"+fmt(summary.totalServiceRevenue),     accent:"#059669", sub:"All services"},
    {icon:"🛏", label:"Room Revenue",    value:"₹"+fmt(summary.totalRoomRevenue),        accent:"#db2777", sub:"All rooms"},
    {icon:"💊",label:"Medicine Revenue", value:"₹"+fmt(summary.totalMedicineRevenue),    accent:"#0d9488", sub:"IPD Pharmacy"},
    {icon:"🧾",label:"Billing Revenue",  value:"₹"+fmt(summary.totalBillingRevenue),     accent:"#3b82f6", sub:"General Billing"},
    {icon:"📉",label:"Hospital Expense", value:"₹"+fmt(summary.totalHospitalExpense),    accent:"#dc2626", sub:"Doctor payouts"}, // ✅ NEW CARD
    {icon:"⚠️",label:"Estimated Due",    value:"₹"+fmt(summary.totalEstimatedDue),       accent:"#dc2626", sub:"After all paid"},
  ] : [];

  const dischargedCards = summary ? [
    {icon:"🏥",label:"Total Discharged", value:summary.totalDischarged??0,               accent:"#0284c7"},
    {icon:"📋",label:"Total Billed",     value:"₹"+fmt(summary.totalBilledAmount),       accent:"#059669"},
    {icon:"✂️",label:"Total Discount",   value:"₹"+fmt(summary.totalDiscount),           accent:"#7c3aed"},
    {icon:"💊",label:"Medicine Revenue", value:"₹"+fmt(summary.totalMedicineRevenue),    accent:"#0d9488", sub:"IPD Pharmacy"},
    {icon:"🧾",label:"Billing Revenue",  value:"₹"+fmt(summary.totalBillingRevenue),     accent:"#3b82f6", sub:"General Billing"},
    {icon:"📉",label:"Hospital Expense", value:"₹"+fmt(summary.totalHospitalExpense),    accent:"#dc2626", sub:"Doctor payouts"}, // ✅ NEW CARD
    {icon:"⚠️",label:"Total Due",        value:"₹"+fmt(summary.totalDue),                accent:"#dc2626"},
  ] : [];

  const cards = tab==="current" ? currentCards : dischargedCards;

  return (
    <div className="rp-wrap">
      <div className="rp-mesh" />

      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-header-icon"><i className="bi bi-clipboard2-data-fill" /></div>
          <div>
            <div className="rp-title">IPD Patient Report</div>
            <div className="rp-sub">Comprehensive inpatient analysis dashboard</div>
          </div>
        </div>

        {tab === "current" && (
          <div className="dc-header-search" style={{ maxWidth: '350px', marginLeft: '20px' }}>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadIpdOptions}
              onChange={setSelectedPatient}
              value={selectedPatient}
              isClearable
              placeholder="🔍 Process Discharge (Search Patient)..."
              styles={headerSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        )}

        <div className="tab-switcher" style={{ marginLeft: 'auto' }}>
          <button className={`tab-btn${tab==="current"?" tab-active":""}`} onClick={()=>setTab("current")}>
            <i className="bi bi-heart-pulse-fill" /> Admitted
          </button>
          <button className={`tab-btn${tab==="discharged"?" tab-active":""}`} onClick={()=>setTab("discharged")}>
            <i className="bi bi-box-arrow-right" /> Discharged
          </button>
        </div>
      </div>

      {selectedPatient && tab === "current" && (
        <div className="dc-body" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h5 style={{ margin: 0, fontSize: '15px', color: '#0f172a', fontWeight: '700' }}><i className="bi bi-person-circle"></i> Discharging: {selectedPatient.label}</h5>
            <button type="button" className="dc-toggle-btn" onClick={() => setShowDischargeForm(!showDischargeForm)} style={{ background: '#0284c7' }}>
              {showDischargeForm ? <><i className="bi bi-eye-slash"></i> Hide Discharge Panel</> : <><i className="bi bi-eye"></i> Show Discharge Panel</>}
            </button>
          </div>

          {error && <div className="rp-error glass-card"><i className="bi bi-exclamation-triangle-fill" /> {error} <button onClick={()=>setError("")}><i className="bi bi-x" /></button></div>}
          {message && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill" /> {message} <button onClick={()=>setMessage("")}><i className="bi bi-x" /></button></div>}
          
          {hasPendingDues && (
             <div className="dc-alert dc-alert-danger dc-fw-bold" style={{ fontSize: '12px', border: '1px solid #dc2626' }}>
               <i className="bi bi-shield-lock-fill" style={{ fontSize: '14px' }}></i>
               <span>Discharge Restricted: Clear pending General Billing or Medicine dues first!</span>
             </div>
          )}

          {showDischargeForm && (
            <div className="dc-layout-split">
              {/* LEFT 70% - DISCHARGE FORM */}
              <div className="dc-layout-left">
                <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleDischargeSubmit(); }}>
                  <div className="dc-section">
                    <div className="dc-section-title"><i className="bi bi-receipt-cutoff"></i><span>Discharge Details</span></div>
                    <div className="dc-section-body">
                      <div className="dc-grid dc-grid-12 dc-mb1">
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Bill Date</label><input type="date" className="dc-input" value={dischargeForm.billDate} onChange={(e) => handleDischargeFormChange("billDate", e.target.value)} /></div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Discharge Date</label><input type="date" className="dc-input" value={dischargeForm.dischargeDate} onChange={(e) => handleDischargeFormChange("dischargeDate", e.target.value)} /></div>
                        <div style={{ gridColumn: 'span 1' }}><label className="dc-label">Days</label><input type="number" className="dc-input" value={dischargeForm.nofDays} readOnly disabled /></div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label className="dc-label dc-clr-success">Grand Total (₹)</label>
                          <input type="number" className="dc-input dc-fw-bold dc-clr-success" value={dischargeForm.totalAmount.toFixed(2)} readOnly disabled />
                        </div>
                        <div style={{ gridColumn: 'span 2' }} className="react-select-container">
                          <label className="dc-label">Discount Type</label>
                          <Select options={[{ value: "amount", label: "Amount (₹)" }, { value: "percent", label: "Percent (%)" }]} value={{ value: dischargeForm.discountType, label: dischargeForm.discountType === "amount" ? "Amount (₹)" : "Percent (%)" }} onChange={(opt) => setDischargeForm(p => ({ ...p, discountType: opt.value, discountValue: 0 }))} styles={ss} menuPortalTarget={document.body} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Discount Value</label><input type="number" className="dc-input" value={dischargeForm.discountValue} onFocus={e=>e.target.select()} onChange={e=>handleDischargeFormChange("discountValue", e.target.value)} min="0" step="0.01" /></div>
                        <div style={{ gridColumn: 'span 1' }}><label className="dc-label">Calc. Disc.</label><input type="number" className="dc-input" value={dischargeForm.discount.toFixed(2)} readOnly disabled /></div>
                      </div>

                      <div className="dc-grid dc-grid-10 dc-mb1">
                        <div style={{ gridColumn: 'span 2' }} className="react-select-container">
                          <label className="dc-label">Dis. Authority</label>
                          <DiscountAuthorityDropdown value={dischargeForm.disAuthority} onChange={(opt) => handleDischargeFormChange("disAuthority", opt ? opt.value : "")} styles={ss} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Total Advance</label><input type="number" className="dc-input" value={dischargeForm.advance.toFixed(2)} readOnly disabled /></div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Payable Amt</label><input type="number" className="dc-input dc-fw-bold" value={dischargeForm.payable.toFixed(2)} readOnly disabled /></div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label">Received Now</label><input type="number" className="dc-input" value={dischargeForm.received} onFocus={e=>e.target.select()} onChange={e=>handleDischargeFormChange("received", e.target.value)} min="0" step="0.01" /></div>
                        <div style={{ gridColumn: 'span 2' }}><label className="dc-label dc-clr-danger">Due Amount</label><input type="number" className="dc-input dc-fw-bold dc-clr-danger" value={dischargeForm.due.toFixed(2)} readOnly disabled /></div>
                      </div>

                      <div className="dc-grid dc-grid-12 dc-mb1 dc-align-end">
                        <div style={{ gridColumn: 'span 3' }}><label className="dc-label">Prepared By <span className="dc-required">*</span></label><input type="text" className="dc-input" value={dischargeForm.docPreparedby} onChange={e=>handleDischargeFormChange("docPreparedby", e.target.value)} placeholder="Enter Name" /></div>
                        <div style={{ gridColumn: 'span 7' }}><label className="dc-label">Remarks</label><textarea className="dc-textarea" rows="1" value={dischargeForm.remarks} onChange={e=>handleDischargeFormChange("remarks", e.target.value)} placeholder="Enter remarks..." /></div>
                        <div style={{ gridColumn: 'span 2' }} className="dc-status-wrap">
                          <label className="dc-status-label"><input type="checkbox" className="dc-checkbox" checked={dischargeForm.patientStatus} onChange={e=>handleDischargeFormChange("patientStatus", e.target.checked)} /> <span className={dischargeForm.patientStatus ? "dc-clr-danger dc-fw-bold" : "dc-clr-success dc-fw-bold"}>{dischargeForm.patientStatus ? "Expired" : "Alive"}</span></label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="dc-form-footer">
                    <button type="submit" className="dc-btn-submit" disabled={dischargingPatient || hasPendingDues}>
                      {dischargingPatient ? <><span className="dc-spinner"></span>Processing...</> : <><i className="bi bi-check-circle"></i> Finalize Discharge</>}
                    </button>
                  </div>
                </form>
              </div>

              {/* RIGHT 30% - FINANCIAL OVERVIEW & HISTORY */}
              <div className="dc-layout-right">
                <div className="dc-section dc-sticky-panel">
                  <div className="dc-section-title" style={{ background: '#0f172a' }}>
                     <i className="bi bi-pie-chart-fill"></i><span>Financial Overview</span>
                  </div>
                  <div className="dc-section-body" style={{ padding: '8px', background: '#f8fafc', maxHeight: '500px', overflowY: 'auto' }}>
                    
                    {/* Package Info */}
                    {finSummary && finSummary.hasPackage ? (
                      <div className="fin-box pkg-box">
                        <div className="fin-box-title"><i className="bi bi-box-seam"></i> Package Applied</div>
                        <div className="fin-box-name">{finSummary.packageName}</div>
                        <div className="fin-box-amt">₹{fmt(finSummary.packageAmount)}</div>
                        <div className="fin-badges">
                          {finSummary.isBillingIncluded && <span className="dc-badge dc-badge-blue">Gen. Billing Incl.</span>}
                          {finSummary.isMedicineIncluded && <span className="dc-badge dc-badge-green">Medicine Incl.</span>}
                        </div>
                        <div className="pkg-breakdown" style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px dashed #fcd34d' }}>
                          <KV k="(-) Services Used" v={`₹${fmt(dischargeForm.serviceTotal)}`} red />
                          <KV k="(-) Room Used"     v={`₹${fmt(dischargeForm.roomTotal)}`} red />
                          {finSummary.isBillingIncluded ? (
                            <KV k="(-) Gen. Billing Exp" v={`₹${fmt(finSummary.billingPayable)}`} red />
                          ) : (
                            <KV k="(i) Gen. Billing" v="Not Included" warning />
                          )}
                          {finSummary.isMedicineIncluded ? (
                            <KV k="(-) Medicine Exp" v={`₹${fmt(finSummary.medicinePayable)}`} red />
                          ) : (
                            <KV k="(i) Medicine" v="Not Included" warning />
                          )}
                          {/* ✅ SHOW EXPENSE DEDUCTION HERE AS WELL */}
                          <KV k="(-) Hosp. Expense" v={`₹${fmt(rightPanelTotalExpense)}`} red />
                        </div>
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #fcd34d' }}>
                          <KV k="Hospital Margin" v={`₹${fmt(pkgMargin)}`} green={pkgMargin >= 0} red={pkgMargin < 0} />
                        </div>
                      </div>
                    ) : (
                      <div className="fin-box nopkg-box"><i className="bi bi-info-circle"></i> No Package Applied</div>
                    )}

                    {/* Gen Billing Status */}
                    <div className="fin-card mt-2">
                       <div className="fin-card-header"><i className="bi bi-receipt"></i> General Billing Status</div>
                       <div className="fin-card-body">
                          <div className="fin-mini-row"><span>Expense (Payable):</span> <span>₹{fmt(finSummary?.billingPayable)}</span></div>
                          <div className="fin-mini-row"><span>Paid (Received):</span> <span className="dc-clr-success">₹{fmt(finSummary?.billingPaid)}</span></div>
                          <div className="fin-mini-row dc-fw-bold">
                            <span>Due Amount:</span> 
                            <span className={finSummary?.billingDue > 0 ? 'dc-clr-danger' : 'dc-clr-success'}>₹{fmt(finSummary?.billingDue)}</span>
                          </div>
                       </div>
                    </div>

                    {/* Medicine Status */}
                    <div className="fin-card mt-2">
                       <div className="fin-card-header"><i className="bi bi-capsule"></i> Medicine Sales Status</div>
                       <div className="fin-card-body">
                          <div className="fin-mini-row"><span>Expense (Payable):</span> <span>₹{fmt(finSummary?.medicinePayable)}</span></div>
                          <div className="fin-mini-row"><span>Paid (Received):</span> <span className="dc-clr-success">₹{fmt(finSummary?.medicinePaid)}</span></div>
                          <div className="fin-mini-row dc-fw-bold">
                            <span>Due Amount:</span> 
                            <span className={finSummary?.medicineDue > 0 ? 'dc-clr-danger' : 'dc-clr-success'}>₹{fmt(finSummary?.medicineDue)}</span>
                          </div>
                       </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FILTERS & CARDS FOR MAIN REPORT PAGE */}
      <div className="filter-bar glass-card">
        <div className="filter-row">
          <div className="filter-group">
            <label><i className="bi bi-calendar-range" /> {tab==="current"?"Admit From":"Discharge From"}</label>
            <input type="date" className="filter-input" value={filters.from} onChange={e=>setFilters(f=>({...f,from:e.target.value}))} />
          </div>
          <div className="filter-group">
            <label><i className="bi bi-calendar-check" /> To</label>
            <input type="date" className="filter-input" value={filters.to} onChange={e=>setFilters(f=>({...f,to:e.target.value}))} />
          </div>
          <div className="filter-group flex-2">
            <label><i className="bi bi-search" /> Search Records</label>
            <input type="text" className="filter-input" placeholder="Name, Mobile, UHID, IPD No..."
              value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))}
              onKeyDown={e=>e.key==="Enter"&&fetchReport()} />
          </div>
          <div className="filter-group flex-15">
            <label><i className="bi bi-grid" /> Department</label>
            <Select options={departments} styles={ss} isClearable placeholder="All Depts"
              menuPortalTarget={document.body}
              value={departments.find(d=>d.value===filters.deptId)||null}
              onChange={o=>setFilters(f=>({...f,deptId:o?o.value:null}))} />
          </div>
          <div className="filter-group flex-15">
            <label><i className="bi bi-person-badge" /> Doctor</label>
            <Select options={doctors} styles={ss} isClearable placeholder="All Doctors"
              menuPortalTarget={document.body}
              value={doctors.find(d=>d.value===filters.doctorId)||null}
              onChange={o=>setFilters(f=>({...f,doctorId:o?o.value:null}))} />
          </div>
          <div className="filter-group btn-group-filter">
            <button className="btn-search" onClick={fetchReport} disabled={loading}>
              {loading?<span className="spin"/>:<i className="bi bi-search"/>}
              {loading?"Loading...":"Search"}
            </button>
            <button className="btn-reset-f" onClick={()=>{
              setFilters({from:"",to:today(),search:"",doctorId:null,deptId:null});
              setData([]); setSummary(null); setRooms([]); setServices([]); setAdvances([]); setExpenses([]);
            }}><i className="bi bi-arrow-clockwise" /></button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="cards-grid">
          {cards.map((c,i) => <SummaryCard key={i} {...c} />)}
        </div>
      )}

      {/* DATA TABLE */}
      {data.length > 0 ? (
        <div className="table-glass glass-card">
          <div className="table-header-row">
            <div>
               <span className="table-count-label"><i className="bi bi-table" /> {data.length} records</span>
               <span className="table-date-label" style={{ marginLeft: '10px' }}>{tab==="current"?"Currently Admitted":"Discharged"} Patients</span>
            </div>
            
            {/* 🖨️ NEW PRINT BUTTON ADDED HERE */}
            <button 
               onClick={handlePrintList}
               style={{
                 background: '#0f172a', color: 'white', border: 'none', padding: '5px 12px', 
                 borderRadius: '5px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer',
                 display: 'flex', alignItems: 'center', gap: '5px'
               }}
            >
              <i className="bi bi-printer"></i> Print List
            </button>
          </div>
          <div className="table-scroll">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Patient</th><th>Contact</th><th>Doctor / Dept</th><th>Dates</th>
                  <th>Room(s)</th>
                  <th>{tab === "current" ? "Current Expenses" : "Discharge Bill"}</th>
                  <th>{tab === "current" ? "Advance / Est. Due" : "Received / Final Due"}</th>
                  <th style={{width:70, textAlign:'center'}}>{tab==="discharged"?"Status":"Expand"}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row,idx) => (
                  <PatientRow key={g(row,"iPD_ID","ipd_ID")||idx}
                    row={row} tab={tab} idx={idx}
                    rooms={rooms} services={services} advances={advances} expenses={expenses} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !loading && summary===null ? (
        <div className="empty-state glass-card">
          <div className="empty-icon">📋</div>
          <div className="empty-title">Apply filters and click Search</div>
          <div className="empty-sub">Use the filter bar above to load report data</div>
        </div>
      ) : !loading && (
        <div className="empty-state glass-card">
          <div className="empty-icon">🔍</div>
          <div className="empty-title">No records found</div>
          <div className="empty-sub">Try adjusting your search filters</div>
        </div>
      )}

      {showReceipt && receiptData && companyDetails && (
        <DischargeReceiptModal
          show={showReceipt}
          data={receiptData}
          patient={receiptData.patient}
          advanceDetails={receiptData.advanceDetails}
          dischargeDetails={receiptData.rawServerResponse?.discharge || receiptData.rawServerResponse}
          serviceDetails={receiptData.services}
          roomDetails={receiptData.roomDetails}
          companyDetails={companyDetails}
          onClose={() => setShowReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        :root {
          --accent: #0ea5e9;
          --bg-deep: #f8fafc; 
          --glass-bg: #ffffff;
          --glass-border: #e2e8f0;
          --glass-hover: #f1f5f9;
          --text-primary: #0f172a;
          --text-muted: #64748b;
          --text-dim: #94a3b8;
          --green: #059669;
          --blue: #0284c7;
          --orange: #d97706;
          --red: #dc2626;
          --teal: #0d9488;
          --purple: #7c3aed;
          --yellow: #ca8a04;
        }
        *{box-sizing:border-box;margin:0;padding:0;}
        .rp-wrap{font-family:'Plus Jakarta Sans',sans-serif;background:var(--bg-deep);min-height:100vh;color:var(--text-primary);position:relative;overflow-x:hidden;padding:0 0 40px;}
        .rp-mesh{position:fixed;inset:0;z-index:0;pointer-events:none;background:radial-gradient(ellipse 60% 40% at 15% 20%,rgba(14,165,233,.05) 0%,transparent 60%),radial-gradient(ellipse 50% 35% at 85% 70%,rgba(124,58,237,.04) 0%,transparent 60%),radial-gradient(ellipse 40% 30% at 50% 90%,rgba(5,150,105,.03) 0%,transparent 60%);}
        .glass-card{position:relative;z-index:1;background:var(--glass-bg);border:1px solid var(--glass-border);border-radius:14px;box-shadow:0 4px 6px -1px rgba(0,0,0,.05),0 2px 4px -1px rgba(0,0,0,.03);}
        .rp-header{position:relative;z-index:2;display:flex;align-items:center;justify-content:space-between;padding:16px 24px;background:#ffffff;border-bottom:1px solid var(--glass-border);box-shadow:0 1px 3px 0 rgba(0,0,0,.05);}
        .rp-header-left{display:flex;align-items:center;gap:14px;}
        .rp-header-icon{width:42px;height:42px;border-radius:10px;background:#e0f2fe;border:1px solid #bae6fd;display:flex;align-items:center;justify-content:center;font-size:18px;color:var(--accent);}
        .rp-title{font-size:17px;font-weight:800;color:#0f172a;line-height:1.2;}
        .rp-sub{font-size:12px;color:var(--text-muted);margin-top:1px;}
        .tab-switcher{display:flex;gap:4px;background:#f1f5f9;border-radius:10px;padding:4px;border:1px solid #e2e8f0;}
        .tab-btn{display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:7px;border:none;font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:var(--text-muted);transition:all .2s;white-space:nowrap;}
        .tab-btn:hover{color:var(--text-primary);background:#e2e8f0;}
        .tab-active{background:#ffffff!important;color:var(--accent)!important;border:1px solid #e2e8f0!important;box-shadow:0 1px 3px rgba(0,0,0,.1);}
        .tab-count{background:var(--accent);color:#ffffff;font-size:10px;font-weight:800;padding:2px 7px;border-radius:20px;}
        .filter-bar{margin:16px;padding:16px; z-index:10;} 
        .filter-row{display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;}
        .filter-group{display:flex;flex-direction:column;gap:5px;}
        .filter-group.flex-2{flex:2;min-width:160px;}.filter-group.flex-15{flex:1.5;min-width:130px;}
        .filter-group.btn-group-filter{flex-direction:row;gap:8px;align-items:flex-end;}
        .filter-group label{font-size:11px;font-weight:600;color:var(--text-muted);display:flex;align-items:center;gap:5px;}
        .filter-input{height:32px;padding:0 12px;border-radius:6px;border:1px solid #cbd5e1;font-size:12px;background:#ffffff;color:var(--text-primary);outline:none;transition:all .2s;width:100%;}
        .filter-input:focus{border-color:var(--accent);box-shadow:0 0 0 2px rgba(14,165,233,.1);}
        .filter-input::placeholder{color:var(--text-dim);}
        .btn-search{height:32px;padding:0 18px;border-radius:6px;border:none;background:linear-gradient(135deg,#0ea5e9,#4f46e5);color:#fff;font-size:12px;font-weight:700;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all .2s;white-space:nowrap;}
        .btn-search:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 12px rgba(14,165,233,.25);}
        .btn-search:disabled{opacity:.6;cursor:not-allowed;}
        .btn-reset-f{height:32px;width:32px;border-radius:6px;border:1px solid #cbd5e1;background:#ffffff;color:var(--text-muted);cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;transition:all .2s;}
        .btn-reset-f:hover{color:var(--red);border-color:var(--red);background:#fef2f2;}
        .spin{width:14px;height:14px;border-radius:50%;border:2px solid rgba(255,255,255,.4);border-top-color:white;animation:spin .7s linear infinite;display:inline-block;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .rp-error{margin:0 16px 16px;padding:12px 16px;display:flex;align-items:center;gap:8px;font-size:13px;color:#991b1b;background:#fef2f2;border-color:#fecaca;}
        .rp-error button{margin-left:auto;background:none;border:none;color:#991b1b;cursor:pointer;font-size:18px;}
        
        .cards-grid{display:grid;grid-template-columns:repeat(auto-fit, minmax(180px, 1fr));gap:12px;margin:0 16px 16px;position:relative;z-index:1;}
        @media(max-width:1200px){.cards-grid{grid-template-columns:repeat(auto-fit, minmax(160px, 1fr));}}
        @media(max-width:700px){.cards-grid{grid-template-columns:repeat(2,1fr);}}
        
        .sum-card{position:relative;overflow:hidden;background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:14px 16px;display:flex;align-items:flex-start;gap:12px;transition:transform .2s,box-shadow .2s;}
        .sum-card:hover{transform:translateY(-2px);box-shadow:0 10px 20px rgba(0,0,0,.06);}
        .sum-glow{position:absolute;bottom:-20px;right:-20px;width:70px;height:70px;border-radius:50%;background:var(--accent);opacity:.08;filter:blur(20px);}
        .sum-icon{font-size:24px;line-height:1;margin-top:2px;}
        .sum-val{font-size:18px;font-weight:800;line-height:1.2;}
        .sum-label{font-size:11px;font-weight:600;color:var(--text-muted);margin-top:4px;text-transform:uppercase;letter-spacing:.5px;}
        .sum-sub{font-size:10px;color:var(--text-dim);margin-top:2px;}
        .table-glass{margin:0 16px;overflow:hidden;}
        .table-header-row{display:flex;align-items:center;justify-content:space-between;padding:12px 18px;border-bottom:1px solid var(--glass-border);background:#f8fafc;}
        .table-count-label{font-size:12px;font-weight:700;color:var(--accent);display:flex;align-items:center;gap:6px;}
        .table-date-label{font-size:12px;color:var(--text-muted);font-weight:500;}
        .table-scroll{overflow-x:auto;max-height:calc(100vh - 320px);overflow-y:auto;}
        .table-scroll::-webkit-scrollbar{width:6px;height:6px;}
        .table-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px;}
        .rp-table{width:100%;border-collapse:collapse;font-size:12px;}
        .rp-table thead tr{background:#f1f5f9;position:sticky;top:0;z-index:2;}
        .rp-table th{padding:10px 12px;text-align:left;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #cbd5e1;white-space:nowrap;}
        .data-row{cursor:pointer;transition:background .15s;border-bottom:1px solid #e2e8f0;animation:rowIn .3s ease both;background:#ffffff;}
        @keyframes rowIn{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:none;}}
        .data-row:hover{background:#f8fafc;}
        .data-row.row-expanded{background:#f0f9ff;border-bottom:none;}
        .rp-table td{padding:10px 12px;vertical-align:middle;}
        .pat-name{font-size:13px;font-weight:700;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
        .pat-srno{font-family:'DM Mono',monospace;font-size:11px;color:#0284c7;background:#e0f2fe;padding:2px 6px;border-radius:4px;border:1px solid #bae6fd;}
        .pat-meta{display:flex;align-items:center;gap:6px;margin-top:4px;}
        .uhid-tag{font-family:'DM Mono',monospace;font-size:11px;color:var(--text-muted);}
        .gender-pill{font-size:10px;font-weight:600;padding:2px 6px;border-radius:4px;}
        .age-tag{font-size:11px;color:var(--text-muted);}
        .pkg-pill{font-size:10px;font-weight:600;color:#b45309;background:#fef3c7;border:1px solid #fde68a;padding:2px 7px;border-radius:10px;display:flex;align-items:center;gap:3px;}
        .doc-wrap{font-size:12px;font-weight:600;display:flex;align-items:center;gap:5px;}
        .doc-wrap i{color:var(--blue);font-size:11px;}
        .deg-tag{font-size:10px;color:var(--text-muted);font-weight:400;}
        .dept-wrap{font-size:11px;color:var(--text-muted);margin-top:3px;}
        .mob-wrap{font-size:12px;display:flex;align-items:center;gap:5px;}
        .mob-wrap i{color:var(--text-muted);font-size:11px;}
        .care-wrap{font-size:11px;color:var(--text-muted);display:flex;align-items:center;gap:5px;margin-top:3px;}
        .care-wrap i{color:var(--text-dim);font-size:10px;}
        .date-chip{font-size:12px;display:flex;align-items:center;gap:5px;}
        .date-chip i{color:var(--text-muted);}
        .los-chip{font-size:11px;color:var(--purple);font-weight:600;display:flex;align-items:center;gap:4px;margin-top:3px;}
        .room-badge-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 8px;border-radius:6px;font-size:11px;margin-bottom:3px;}
        .room-badge-compact{padding:2px 6px;font-size:10px;margin-bottom:2px;}
        .room-cat-small{font-size:10px;opacity:.8;}
        .live-dot-sm{font-size:8px;color:#059669;margin-left:2px;}
        .multi-room{display:flex;flex-direction:column;gap:3px;}
        .no-room{font-size:11px;color:var(--text-dim);font-style:italic;}
        .amt-col,.adv-col{min-width:130px;}
        .amt-row{font-size:11px;display:flex;align-items:center;gap:5px;padding:1.5px 0;font-family:'DM Mono',monospace;}
        .amt-row i{font-size:10px;}
        .amt-row.green{color:var(--green);}.amt-row.blue{color:var(--blue);}.amt-row.orange{color:var(--orange);}.amt-row.red{color:var(--red);}.amt-row.teal{color:var(--teal);}.amt-row.purple{color:var(--purple);}
        .total-row{font-weight:700;font-size:12px;margin-top:2px;}
        .svc-count-btn,.adv-count-btn{margin-left:5px;font-size:10px;font-weight:700;border:none;border-radius:4px;padding:2px 6px;cursor:pointer;display:inline-flex;align-items:center;gap:3px;transition:all .15s;}
        .svc-count-btn{background:#d1fae5;color:var(--green);}
        .svc-count-btn:hover{background:var(--green);color:#ffffff;}
        .adv-count-btn{background:#ccfbf1;color:var(--teal);}
        .adv-count-btn:hover{background:var(--teal);color:#ffffff;}
        .status-pill{font-size:10px;font-weight:700;padding:3px 8px;border-radius:10px;display:flex;align-items:center;gap:4px;white-space:nowrap;margin-bottom:4px;width:fit-content;margin:0 auto 4px;}
        .recovered{background:#d1fae5;color:#059669;border:1px solid #a7f3d0;}
        .deceased{background:#fee2e2;color:#dc2626;border:1px solid #fecaca;}
        .expand-col{text-align:center;}
        .expand-btn{width:26px;height:26px;border-radius:6px;border:1px solid #cbd5e1;background:#ffffff;color:var(--text-muted);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:12px;transition:all .2s;}
        .expand-btn:hover{background:#e0f2fe;color:var(--accent);border-color:#bae6fd;}
        .detail-row td{padding:0 12px 16px; background:#f0f9ff;}
        .detail-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;background:#ffffff;border:1px solid #e2e8f0;border-radius:10px;padding:16px;box-shadow:inset 0 2px 4px 0 rgba(0,0,0,.02);}
        .detail-block-title{font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;letter-spacing:.5px;display:flex;align-items:center;gap:6px;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #e2e8f0;}
        .detail-total-badge{margin-left:auto;font-size:11px;background:#ccfbf1;color:var(--teal);border:1px solid #99f6e4;padding:2px 8px;border-radius:10px;}
        .detail-kv{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;font-size:11px;padding:3px 0;}
        .detail-kv span:first-child{color:var(--text-muted);white-space:nowrap;}
        .detail-kv span:last-child{color:var(--text-primary);text-align:right;font-weight:600;}
        .detail-kv.highlight{background:#f0f9ff;border-radius:4px;padding:4px 6px;margin:2px -6px;}
        .detail-kv.highlight span{color:var(--accent)!important;}
        .detail-kv.red-kv span:last-child{color:var(--red);}
        .detail-kv.green-kv span:last-child{color:var(--green);}
        .detail-kv.teal-kv span:last-child{color:var(--teal);}
        .detail-kv.warning-kv span:last-child{color:var(--orange); font-style:italic;}
        .pkg-block{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px 12px;}
        .pkg-breakdown{margin-top:8px; padding-top:6px; border-top:1px dashed #fcd34d; padding-left: 8px;}
        .no-data-small{font-size:11px;color:var(--text-dim);font-style:italic;padding:4px 0;}
        .room-detail-row{padding:6px 10px;margin-bottom:6px;border-radius:6px;background:#f8fafc;border:1px solid #e2e8f0;}
        .room-detail-top{display:flex;align-items:center;gap:8px;margin-bottom:4px;}
        .room-cat-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:5px;}
        .room-no-tag{font-family:'DM Mono',monospace;font-size:11px;color:var(--text-primary);font-weight:600;}
        .live-dot{font-size:10px;color:#059669;font-weight:700;}
        .room-detail-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:10px;color:var(--text-muted);}
        .room-days{color:var(--text-muted);}
        .room-charge-tag{color:var(--orange);font-weight:700;font-family:'DM Mono',monospace;}
        .room-total-row{display:flex;justify-content:space-between;font-size:11px;font-weight:700;padding:6px 10px;background:#fff7ed;border-radius:6px;margin-top:6px;color:var(--orange);border:1px solid #ffedd5;}
        .adv-detail-row{padding:6px 10px;margin-bottom:6px;border-radius:6px;background:#f8fafc;border-left:3px solid var(--teal); border-top:1px solid #e2e8f0; border-right:1px solid #e2e8f0; border-bottom:1px solid #e2e8f0;}
        .adv-detail-top{display:flex;align-items:center;gap:10px;margin-bottom:3px;}
        .adv-amt{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:var(--teal);}
        .mode-pill{font-size:10px;font-weight:600;background:#ccfbf1;color:var(--teal);border:1px solid #99f6e4;padding:2px 8px;border-radius:5px;}
        .adv-detail-date{font-size:10px;color:var(--text-muted);display:flex;align-items:center;gap:5px;}
        .adv-remarks{font-size:10px;color:var(--text-dim);font-style:italic;margin-top:3px;}
        .btn-view-svc{margin-top:10px;width:100%;padding:6px 0;border-radius:6px;border:1px solid #a7f3d0;background:#d1fae5;color:var(--green);font-size:11px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:all .2s;}
        .btn-view-svc:hover{background:#a7f3d0;color:#065f46;}
        .empty-state{margin:24px 16px;padding:48px;text-align:center;background:#ffffff;}
        .empty-icon{font-size:48px;margin-bottom:16px;color:#94a3b8;}
        .empty-title{font-size:16px;font-weight:700;margin-bottom:6px;color:#0f172a;}
        .empty-sub{font-size:13px;color:var(--text-muted);}
        
        /* ── Discharge Form / Split Layout ── */
        .dc-layout-split { display: flex; gap: 15px; align-items: flex-start; margin-bottom: 20px;}
        .dc-layout-left { flex: 0 0 68%; width: 68%; }
        .dc-layout-right { flex: 0 0 calc(32% - 15px); width: calc(32% - 15px); position: sticky; top: 10px; }
        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 6px; margin-bottom: 8px; overflow: visible; }
        .dc-sticky-panel { box-shadow: 0 4px 12px rgba(0,0,0,0.05); border-color: #cbd5e0; }
        .dc-section-title { background: var(--theme-color, #0088cc); padding: 4px 10px; display: flex; align-items: center; gap: 6px; border-radius: 5px 5px 0 0; }
        .dc-section-title i, .dc-section-title span { font-size: 11px; color: #fff; font-weight: 600; }
        .dc-section-body { padding: 8px; background: #fff; border-radius: 0 0 6px 6px; }
        .dc-grid { display: grid; gap: 6px; align-items: start; }
        .dc-grid-12 { grid-template-columns: repeat(12, 1fr); }
        .dc-grid-10 { grid-template-columns: repeat(10, 1fr); }
        .dc-mb1 { margin-bottom: 6px; }
        .dc-align-end { align-items: end; }
        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 1px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .dc-clr-success { color: #16a34a !important; }
        .dc-clr-danger  { color: #dc2626 !important; }
        .dc-fw-bold     { font-weight: 700 !important; }
        .dc-required    { color: #e74c3c; }
        .dc-input, .dc-textarea, .dc-table-input { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; line-height: 1; }
        .dc-textarea { resize: none; overflow-y: auto; padding-top: 4px; }
        .dc-input:focus, .dc-textarea:focus, .dc-table-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%); }
        .dc-input:disabled, .dc-textarea:disabled { background: #f3f4f6; color: #555; cursor: not-allowed; }
        .dc-status-wrap { display: flex; align-items: center; padding-bottom: 2px; }
        .dc-status-label { display: flex; align-items: center; gap: 5px; font-size: 11px; cursor: pointer; }
        .dc-checkbox { margin: 0; cursor: pointer; }
        .dc-form-footer { margin-top: 6px; padding-top: 8px; border-top: 1px solid #e0e6ed; text-align: right; }
        .dc-btn-submit { padding: 5px 18px; border-radius: 5px; border: none; background: var(--theme-color, #0088cc); color: #fff; font-size: 11px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: opacity 0.15s, transform 0.15s, box-shadow 0.15s; }
        .dc-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; filter: grayscale(1); }
        .dc-alert { display: flex; align-items: center; gap: 7px; padding: 6px 10px; border-radius: 5px; font-size: 11px; margin-bottom: 8px; position: relative; animation: dc-slidein 0.25s ease; }
        .dc-alert-danger  { background: #fff5f5; color: #c53030; border: 1px solid #fc8181; }
        .dc-alert-success { background: #f0fdf4; color: #22543d; border: 1px solid #68d391; }
        .dc-alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; font-size: 14px; color: inherit; line-height: 1; }
        .dc-header-search { flex: 1 1 0; min-width: 0; }
        .dc-toggle-btn { flex-shrink: 0; padding: 3px 10px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.5); background: rgba(255,255,255,0.15); color: #fff; font-size: 11px; font-weight: 600; cursor: pointer; transition: background 0.15s; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
        .dc-toggle-btn:hover { background: rgba(255,255,255,0.3); }
        
        /* ── Financial Panel Styling ── */
        .fin-box { padding: 10px; border-radius: 5px; margin-bottom: 12px; border: 1px solid transparent; }
        .fin-box-title { font-size: 10px; color: #3b82f6; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
        .fin-box-name { font-size: 13px; font-weight: 700; color: #1e3a8a; line-height: 1.2; }
        .fin-box-amt { font-size: 16px; font-weight: 800; color: #2563eb; margin: 4px 0; }
        .fin-badges { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px; }
        .dc-badge { padding: 2px 6px; border-radius: 12px; font-size: 9px; font-weight: 600; }
        .dc-badge-blue { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .dc-badge-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .fin-card { border: 1px solid #e2e8f0; border-radius: 5px; overflow: hidden; }
        .fin-card-header { background: #f1f5f9; padding: 4px 8px; font-size: 10px; font-weight: 700; color: #334155; border-bottom: 1px solid #e2e8f0; }
        .fin-card-body { padding: 6px 8px; background: #fff; }
        .fin-mini-row { display: flex; justify-content: space-between; font-size: 10px; padding: 2px 0; color: #475569; }
        .history-table { width: 100%; font-size: 9px; border-collapse: collapse; }
        .history-table th { background: #f1f5f9; padding: 4px; text-align: left; color: #475569; font-weight: 700; border-bottom: 1px solid #e2e8f0; }
        .history-table td { padding: 4px; border-bottom: 1px solid #f1f5f9; color: #334155; }
        .type-badge { padding: 1px 4px; border-radius: 4px; font-size: 8px; font-weight: bold; }
        .type-badge.invoice { background: #e0f2fe; color: #0284c7; }
        .type-badge.payment { background: #dcfce7; color: #166534; }
        .type-badge.pharmacy { background: #fce7f3; color: #db2777; }
        
        .popup-overlay{position:fixed;inset:0;background:rgba(15,23,42,.6);z-index:10000;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);padding:16px;}
        .popup-box{background:#ffffff;border:1px solid #e2e8f0;border-radius:14px;width:100%;max-width:900px;max-height:85vh;display:flex;flex-direction:column;box-shadow:0 24px 48px rgba(0,0,0,.15);animation:popIn .2s ease;}
        @keyframes popIn{from{opacity:0;transform:scale(.96);}to{opacity:1;transform:scale(1);}}
        .popup-header{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #e2e8f0;background:#f8fafc;border-radius:14px 14px 0 0;}
        .popup-title{font-size:15px;font-weight:700;color:var(--accent);display:flex;align-items:center;gap:8px;}
        .popup-close{background:none;border:none;color:var(--text-muted);cursor:pointer;font-size:18px;padding:0;display:flex;transition:color .15s;}
        .popup-close:hover{color:var(--red);}
        .popup-body{overflow-y:auto;padding:16px;}
        .popup-body::-webkit-scrollbar{width:6px;}
        .popup-body::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px;}
        .popup-empty{text-align:center;padding:40px;color:var(--text-muted);font-size:14px;display:flex;align-items:center;justify-content:center;gap:8px;}
        .popup-table{width:100%;border-collapse:collapse;font-size:12px;}
        .popup-table thead tr{background:#f1f5f9;}
        .popup-table th{padding:10px 12px;text-align:left;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;border-bottom:1px solid #cbd5e1;}
        .popup-table td{padding:10px 12px;border-bottom:1px solid #e2e8f0;vertical-align:middle;color:#0f172a;}
        .popup-table tbody tr:hover{background:#f8fafc;}
        .popup-table tfoot td{padding:12px 12px;border-top:2px solid #cbd5e1;font-weight:700;}
        .total-label{color:var(--text-muted);font-size:11px;text-transform:uppercase;}
        .total-val{font-size:15px;font-family:'DM Mono',monospace;}
        .amt-green{color:var(--green);font-family:'DM Mono',monospace;font-weight:600;}
        .amt-teal{color:var(--teal);font-family:'DM Mono',monospace;font-weight:600;}
        .adv-total-banner{background:#f0fdfa;border:1px solid #99f6e4;border-radius:8px;padding:12px 16px;margin-bottom:16px;display:flex;align-items:center;gap:10px;font-size:14px;color:#0f766e;}
        .adv-total-banner strong{font-size:18px;font-family:'DM Mono',monospace;}
        .adv-count-badge{margin-left:auto;background:#ccfbf1;border:1px solid #5eead4;padding:2px 10px;border-radius:12px;font-size:11px;color:#0f766e;font-weight:600;}
        
        
        .amt-label {
          color: var(--text-muted);
          font-size: 10px;
          font-weight: 600;
          width: 55px; /* Isse sabhi numbers ek line me align ho jayenge */
          display: inline-block;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
  
        @media (max-width: 900px) {
          .dc-layout-split { flex-direction: column; }
          .dc-layout-left, .dc-layout-right { flex: 0 0 100%; width: 100%; position: static; }
          .dc-grid-12 { grid-template-columns: repeat(6, 1fr); }
          .dc-grid-10 { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 600px) {
          .dc-grid-12, .dc-grid-10 { grid-template-columns: repeat(2, 1fr); }
          .dc-grid-12 > div, .dc-grid-10 > div { grid-column: span 1 !important; }
        }
      `}</style>
    </div>
  );
};

export default IpdReport;  